/**
 * WritingStyleSelector Component (T052-T054, T072, T078)
 *
 * Dropdown component for selecting a writing style for a topic.
 * Only renders if user has completed writing styles available.
 *
 * T072: Added ARIA labels and keyboard navigation support
 * T078: Memoized dropdown options for performance
 */

import { memo, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { IWritingStyleSelectorProps, IWritingStyleSummary } from '@/@types/writingStyle';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

const WritingStyleSelector = ({ topicId, value, onChange }: IWritingStyleSelectorProps) => {
  // Fetch user's writing styles
  const styles = useQuery(api.writingStyles.getMyWritingStyles);
  const updateTopicWritingStyle = useMutation(api.topics.updateTopicWritingStyle);

  // T054: Filter to only show completed styles
  const completedStyles = useMemo((): IWritingStyleSummary[] => {
    if (!styles) return [];
    return styles.filter((style: IWritingStyleSummary) => style.status === 'completed');
  }, [styles]);

  // T078: Memoize dropdown options to prevent unnecessary re-renders
  const dropdownOptions = useMemo(() => {
    return completedStyles.map((style: IWritingStyleSummary) => ({
      id: style._id,
      name: style.name,
    }));
  }, [completedStyles]);

  // T053: Handle style selection
  const handleValueChange = useCallback(
    async (selectedValue: string) => {
      const styleId = selectedValue === 'none' ? null : (selectedValue as typeof value);

      try {
        const result = await updateTopicWritingStyle({
          topicId,
          writingStyleId: styleId ?? null,
        });

        if (result.success) {
          onChange(styleId === null ? undefined : styleId);
        } else {
          console.error('Failed to update writing style:', result.error);
        }
      } catch (error) {
        console.error('Error updating writing style:', error);
      }
    },
    [topicId, updateTopicWritingStyle, onChange]
  );

  // T054: Don't render if loading or no completed styles
  if (styles === undefined) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Loading styles...</span>
      </div>
    );
  }

  // T054: Don't render if user has no completed writing styles
  if (completedStyles.length === 0) {
    return null;
  }

  const currentValue = value || 'none';

  const selectId = `writing-style-select-${topicId}`;

  return (
    <Select value={currentValue} onValueChange={handleValueChange} name={selectId}>
      <SelectTrigger
        className="w-[160px] h-7 text-[13px] rounded-md bg-style-dropdown-bg border-style-dropdown-border"
        aria-label="Writing style"
      >
        <SelectValue placeholder="Default style" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">Default style</SelectItem>
        {dropdownOptions.map(option => (
          <SelectItem key={option.id} value={option.id}>
            {option.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export const WritingStyleSelectorMemo = memo(WritingStyleSelector);
